import { useState, useRef, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Loader2, CheckCircle2, XCircle, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "../lib/apiConfig";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get("email") || "";
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [status, setStatus] = useState("input"); // input | verifying | success | error
  const [message, setMessage] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, "").slice(0, 6).split("");
      digits.forEach((d, i) => {
        if (i + index < 6) newCode[i + index] = d;
      });
      setCode(newCode);
      const nextIdx = Math.min(index + digits.length, 5);
      inputRefs.current[nextIdx]?.focus();
      if (newCode.every((d) => d !== "")) {
        submitCode(newCode.join(""));
      }
      return;
    }
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (newCode.every((d) => d !== "")) {
      submitCode(newCode.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const submitCode = async (fullCode) => {
    if (!email) {
      setStatus("error");
      setMessage("No email address found. Please sign up again.");
      return;
    }
    setStatus("verifying");
    try {
      const res = await axios.post(`${API_BASE}/auth/verify-code`, {
        email,
        code: fullCode,
      });
      setStatus("success");
      setMessage(res.data.message);
    } catch (err) {
      setStatus("error");
      setMessage(
        err.response?.data?.detail ||
          "Verification failed. Please check your code and try again."
      );
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setResendLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/resend-verification`, {
        email,
      });
      if (res.data.code) {
        toast.info(`Your verification code is: ${res.data.code}`, { duration: 30000 });
      } else {
        toast.success(res.data.message || "New code sent!");
      }
      setCode(["", "", "", "", "", ""]);
      setStatus("input");
      setMessage("");
      inputRefs.current[0]?.focus();
    } catch {
      toast.error("Failed to resend. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleRetry = () => {
    setCode(["", "", "", "", "", ""]);
    setStatus("input");
    setMessage("");
    inputRefs.current[0]?.focus();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="fixed inset-0 paper-texture pointer-events-none" />
      <header className="p-4">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src="/icon-192.png" alt="Culture Ken" className="w-10 h-10 rounded-xl" />
          <span className="text-xl font-bold text-foreground">Culture Ken</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg" data-testid="verify-email-card">
          {status === "success" ? (
            <>
              <CardHeader className="text-center pb-4">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <CardTitle className="text-2xl font-bold">Email Verified!</CardTitle>
                <CardDescription className="text-base">{message}</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button
                  className="w-full h-12 text-lg rounded-xl"
                  onClick={() => navigate("/login")}
                  data-testid="goto-login-after-verify"
                >
                  Sign In
                </Button>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-center pb-4">
                {status === "verifying" ? (
                  <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                ) : status === "error" ? (
                  <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-7 h-7 text-primary" />
                  </div>
                )}
                <CardTitle className="text-2xl font-bold">
                  {status === "verifying"
                    ? "Verifying..."
                    : status === "error"
                    ? "Verification Failed"
                    : "Enter Verification Code"}
                </CardTitle>
                <CardDescription className="text-base">
                  {status === "error"
                    ? message
                    : email
                    ? <>We sent a 6-digit code to <strong>{email}</strong></>
                    : "Enter the code from your email"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex justify-center gap-2" data-testid="code-input-group">
                  {code.map((digit, i) => (
                    <Input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={digit}
                      onChange={(e) => handleChange(i, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(i, e)}
                      className="w-12 h-14 text-center text-xl font-mono font-bold"
                      disabled={status === "verifying"}
                      data-testid={`code-input-${i}`}
                    />
                  ))}
                </div>

                {status === "error" && (
                  <Button
                    variant="outline"
                    className="w-full h-12 rounded-xl"
                    onClick={handleRetry}
                    data-testid="retry-code-btn"
                  >
                    Try Again
                  </Button>
                )}

                <div className="text-center space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Didn't receive a code? Check your spam folder.
                  </p>
                  <Button
                    variant="ghost"
                    className="text-sm"
                    onClick={handleResend}
                    disabled={resendLoading}
                    data-testid="resend-code-btn"
                  >
                    {resendLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      "Resend Code"
                    )}
                  </Button>
                </div>

                <Button
                  asChild
                  variant="ghost"
                  className="w-full h-10 rounded-xl"
                >
                  <Link to="/login" data-testid="back-to-login-link">
                    Back to Sign In
                  </Link>
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
